import type { ScheduleCell, ScheduleCellKind } from '@runhuman/shared';

const TIC_EVERY_HOURS = 6;

interface ScheduleStripProps {
  readonly cells: readonly ScheduleCell[];
}

/**
 * Activity-side mirror of the web `ScheduleStrip`. Renders the same 24-hour
 * forward window the web dialog renders — `cells` come from the shared
 * `computeScheduleWindow` helper so both surfaces project the merged
 * schedule the same way. Tic labels show local-time hours every six cells.
 *
 * Three cell variants (#4062): `on-shift` (filled primary), `on-break`
 * (filled break-color so admins can spot scheduled step-away time at a
 * glance), and `off` (background-tertiary). The web sibling collapses
 * on-break onto on-shift visually — distinguishing breaks is Activity-only
 * for now.
 */
export function ScheduleStrip({ cells }: ScheduleStripProps) {
  return (
    <div className="profile-modal__schedule">
      <div className="profile-modal__schedule-strip">
        {cells.map((cell, idx) => (
          <div
            key={cell.startUtc.toISOString()}
            className={cellClass(cell.kind)}
            title={formatLocalHour(cell.startUtc)}
            aria-label={`${cellAriaPrefix(cell.kind)} ${formatLocalHour(cell.startUtc)}`}
            data-tic={idx % TIC_EVERY_HOURS === 0 ? formatLocalHour(cell.startUtc) : undefined}
          />
        ))}
      </div>
      <div className="profile-modal__schedule-tics" aria-hidden="true">
        {cells.map((cell, idx) =>
          idx % TIC_EVERY_HOURS === 0 ? (
            <span key={cell.startUtc.toISOString()} className="profile-modal__schedule-tic">
              {formatLocalHour(cell.startUtc)}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

function cellClass(kind: ScheduleCellKind): string {
  const base = 'profile-modal__schedule-cell';
  switch (kind) {
    case 'on-shift':
      return `${base} ${base}--on-shift`;
    case 'on-break':
      return `${base} ${base}--on-break`;
    case 'off':
      return base;
  }
}

function cellAriaPrefix(kind: ScheduleCellKind): string {
  switch (kind) {
    case 'on-shift':
      return 'On shift';
    case 'on-break':
      return 'Scheduled break';
    case 'off':
      return 'Off shift';
  }
}

function formatLocalHour(utc: Date): string {
  return utc.toLocaleTimeString(undefined, {
    hour: 'numeric',
    hour12: true,
  });
}
